import { z } from "zod";
import { messageRoleSchema } from "../enums.js";
import { isoDateTimeSchema, uuidSchema } from "../primitives.js";

export const conversationSummarySchema = z.object({
  id: uuidSchema,
  title: z.string().nullable(),
  created_at: isoDateTimeSchema,
  updated_at: isoDateTimeSchema,
});

export type ConversationSummary = z.infer<typeof conversationSummarySchema>;

export const conversationListResponseSchema = z.object({
  items: z.array(conversationSummarySchema),
  next_cursor: z.string().nullable(),
});

export type ConversationListResponse = z.infer<
  typeof conversationListResponseSchema
>;

export const conversationMessageSchema = z.object({
  id: uuidSchema,
  role: messageRoleSchema,
  content: z.string(),
  tool_name: z.string().nullable().optional(),
  tool_call_id: z.string().nullable().optional(),
  meta: z.record(z.unknown()).nullable().optional(),
  created_at: isoDateTimeSchema,
});

export type ConversationMessage = z.infer<typeof conversationMessageSchema>;

export const conversationDetailSchema = conversationSummarySchema.extend({
  messages: z.array(conversationMessageSchema),
});

export type ConversationDetail = z.infer<typeof conversationDetailSchema>;

/** SSE payload shapes from POST /v1/chat/stream (WP23). */
export const chatSseMetaSchema = z.object({
  type: z.literal("meta"),
  conversation_id: uuidSchema,
  agent_run_id: uuidSchema,
});

export const chatSseTokenSchema = z.object({
  type: z.literal("token"),
  text: z.string(),
});

export const chatSseToolCallSchema = z.object({
  type: z.literal("tool_call"),
  call_id: z.string(),
  name: z.string(),
  arguments: z.string(),
});

export const chatSseToolResultSchema = z.object({
  type: z.literal("tool_result"),
  call_id: z.string(),
  name: z.string(),
  ok: z.boolean(),
  summary: z.string(),
});

export const chatSseDoneSchema = z.object({
  type: z.literal("done"),
  conversation_id: uuidSchema,
  agent_run_id: uuidSchema,
  steps: z.number().int().nonnegative(),
});

export const chatSseErrorSchema = z.object({
  type: z.literal("error"),
  code: z.string(),
  message: z.string(),
});

export const chatSseEventSchema = z.discriminatedUnion("type", [
  chatSseMetaSchema,
  chatSseTokenSchema,
  chatSseToolCallSchema,
  chatSseToolResultSchema,
  chatSseDoneSchema,
  chatSseErrorSchema,
]);

export type ChatSseEvent = z.infer<typeof chatSseEventSchema>;
